import { Component, computed, DestroyRef, effect, inject, input, output } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { of } from 'rxjs';

import { ButtonModule } from '@openng/optimus-ui/button';
import { FloatLabelModule } from '@openng/optimus-ui/floatlabel';
import { InputTextModule } from '@openng/optimus-ui/inputtext';

import { DropdownData, GenericDropdownComponent, rxResourceFixed } from '@zayan-erp/shared';
import { DsmApsService } from '../../services/dsm-aps.service';
import { DsmPackageService } from '../../services/dsm-package.service';
import { DsmStateService } from '../../services/dsm-state.service';
import { DsmPackageProjectModelElevationOption } from '../../models/dsm-package.models';

@Component({
  selector: 'dsm-option-package-form',
  templateUrl: './dsm-option-package-form.component.html',
  host: { class: 'flex flex-col flex-1 overflow-hidden' },
  imports: [ReactiveFormsModule, ButtonModule, FloatLabelModule, InputTextModule, GenericDropdownComponent]
})
export class DsmOptionPackageFormComponent {
  readonly #dsmApsService = inject(DsmApsService);
  readonly #packageService = inject(DsmPackageService);
  readonly #dsmState = inject(DsmStateService);
  readonly #destroyRef = inject(DestroyRef);

  readonly projectId = input<string | null>(null);
  readonly saved = output<string>();
  readonly cancelled = output<void>();

  readonly form = new FormGroup({
    packageName: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    projectId: new FormControl<string | null>(null, { validators: [Validators.required] }),
    elevationId: new FormControl<string | null>(null, { validators: [Validators.required] }),
    discountPercentage: new FormControl<number | null>(null, {
      validators: [Validators.required, Validators.min(0), Validators.max(100)]
    })
  });

  readonly selectedProjectIdSig = toSignal(this.form.controls.projectId.valueChanges, {
    initialValue: this.form.controls.projectId.value
  });
  readonly formStatus = toSignal(this.form.statusChanges, { initialValue: this.form.status });
  readonly isFormValid = computed(() => this.formStatus() === 'VALID');

  readonly projectsResource = rxResourceFixed<DropdownData[], { salesOfficeId: string | null }>({
    params: () => ({ salesOfficeId: this.#dsmState.selectedSalesOfficeId() }),
    defaultValue: [] as DropdownData[],
    stream: ({ params }) => {
      if (!params.salesOfficeId) return of([] as DropdownData[]);
      return this.#dsmApsService.getSalesOfficeProjects(params.salesOfficeId, true);
    }
  });

  readonly projects = computed<DropdownData[]>(() => this.projectsResource.value() ?? []);

  readonly modelElevationsResource = rxResourceFixed({
    params: () => ({ projectId: this.selectedProjectIdSig() }),
    defaultValue: [] as DsmPackageProjectModelElevationOption[],
    stream: ({ params }) => {
      if (!params.projectId) return of([] as DsmPackageProjectModelElevationOption[]);
      return this.#packageService.getProjectModelElevations(params.projectId);
    }
  });

  readonly modelElevationItems = computed<DropdownData[]>(() =>
    (this.modelElevationsResource.value() ?? []).map(item => ({
      id: item.elevationId,
      firstLabel: `${item.modelName} (${item.elevationCode})`,
      data: item.modelId
    }))
  );

  constructor() {
    effect(() => {
      const projectId = this.projectId();
      if (projectId && !this.form.controls.projectId.value) {
        this.form.controls.projectId.setValue(projectId);
      }
    });

    effect(() => {
      if (this.projectsResource.isLoading()) return;
      const projs = this.projects();
      if (!projs.length) return;
      const currentId = this.form.controls.projectId.value;
      const stillValid = !!currentId && projs.some(p => p.id === currentId);
      if (!stillValid) {
        this.form.controls.projectId.setValue(projs[0].id);
      }
    });

    this.form.controls.projectId.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      this.form.controls.elevationId.setValue(null);
    });
  }

  cancel(): void {
    this.cancelled.emit();
  }

  save(): void {
    if (this.form.invalid) return;

    const { packageName, elevationId, discountPercentage } = this.form.getRawValue();
    if (!elevationId) return;
    const modelId = this.modelElevationItems().find(o => o.id === elevationId)?.data as string | undefined;
    if (!modelId) return;

    this.#packageService
      .createPackage({
        name: packageName.trim(),
        modelId,
        elevationId,
        discountPercentage
      })
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(created => {
        this.#packageService.triggerRefresh();
        this.saved.emit(created?.id ?? '');
      });
  }
}
